/*
 * <hats-print-customizer> — front-only print builder for hats.
 * Adds the hat (with uploaded artwork + text as line-item properties) plus one line item
 * per selected print add-on, all linked by a shared hidden `_ref`.
 * Same cart contract as <print-customizer>; see docs/flanelki-configurator-reference.md.
 */
if (!customElements.get('hats-print-customizer')) {
  customElements.define(
    'hats-print-customizer',
    class HatsPrintCustomizer extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.sectionId;
        this.cartAddUrl = this.dataset.cartAddUrl;
        this.moneyFormat = this.dataset.moneyFormat;
        this.maxMb = parseFloat(this.dataset.maxFileMb) || 5;
        this.maxBytes = this.maxMb * 1024 * 1024;

        this.i18n = JSON.parse(this.querySelector('.pc-i18n-data').textContent);

        this.addons = {};
        JSON.parse(this.querySelector('.pc-addons-data').textContent).forEach((v) => {
          if (v.sku) this.addons[v.sku] = v;
        });
        this.garmentPrices = {};
        JSON.parse(this.querySelector('.pc-garment-data').textContent).forEach((v) => {
          this.garmentPrices[v.id] = v.price;
        });
        this.garmentPrice = parseInt(this.dataset.garmentPrice, 10) || 0;

        this.summaryPrint = this.querySelector('.pc-print-price');
        this.summaryGarment = this.querySelector('.pc-garment-price');
        this.summaryTotal = this.querySelector('.pc-total-price');
        this.errorEl = this.querySelector('.pc-error');
        this.submitButton = this.querySelector('.pc-submit');
        this.spinner = this.querySelector('.loading__spinner');
      }

      connectedCallback() {
        this.productForm = document.getElementById(this.dataset.productFormId);
        this.hideNativeButtons();
        this.wireFileInputs();
        this.addEventListener('change', () => this.updateSummary());
        this.addEventListener('input', (e) => {
          if (e.target.classList.contains('pc-text-input')) this.updateSummary();
        });
        this.submitButton.addEventListener('click', () => this.onSubmit());

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantUnsub = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const variant = event.data && event.data.variant;
            if (variant && this.garmentPrices[variant.id] != null) {
              this.garmentPrice = this.garmentPrices[variant.id];
              this.updateSummary();
            }
          });
        }
        this.updateSummary();
      }

      disconnectedCallback() {
        if (this.variantUnsub) this.variantUnsub();
      }

      hideNativeButtons() {
        if (!this.productForm) return;
        const buttons = this.productForm.querySelector('.product-form__buttons');
        if (buttons) buttons.style.display = 'none';
      }

      wireFileInputs() {
        this.querySelectorAll('.pc-file').forEach((input) => {
          const dropzone = input.closest('.pc-upload');
          const filename = dropzone.querySelector('.pc-upload__filename');

          input.addEventListener('change', () => this.handleFile(input, filename));

          ['dragover', 'dragenter'].forEach((evt) =>
            dropzone.addEventListener(evt, (e) => {
              e.preventDefault();
              dropzone.classList.add('is-dragover');
            })
          );
          ['dragleave', 'dragend', 'drop'].forEach((evt) =>
            dropzone.addEventListener(evt, () => dropzone.classList.remove('is-dragover'))
          );
          dropzone.addEventListener('drop', (e) => {
            e.preventDefault();
            if (e.dataTransfer.files.length) {
              input.files = e.dataTransfer.files;
              this.handleFile(input, filename);
            }
          });
        });
      }

      handleFile(input, filename) {
        const file = input.files && input.files[0];
        if (!file) {
          filename.hidden = true;
          return;
        }
        if (file.size > this.maxBytes) {
          this.showError(this.i18n.fileTooLarge.replace('[name]', file.name).replace('[mb]', this.maxMb));
          input.value = '';
          filename.hidden = true;
          return;
        }
        this.clearError();
        filename.textContent = file.name;
        filename.hidden = false;
      }

      // ---- Pricing ----------------------------------------------------------
      selectedAddons() {
        const selected = [];
        this.querySelectorAll('.pc-check').forEach((c) => {
          if (c.checked) selected.push({ sku: c.dataset.sku, label: c.dataset.label });
        });
        this.querySelectorAll('.pc-text-input').forEach((t) => {
          if (t.value.trim()) selected.push({ sku: t.dataset.sku, label: t.dataset.prop, text: t.value.trim() });
        });
        return selected.filter((a) => this.addons[a.sku]);
      }

      updateSummary() {
        const printCents = this.selectedAddons().reduce((sum, a) => sum + this.addons[a.sku].price, 0);
        this.summaryPrint.textContent = this.formatMoney(printCents);
        this.summaryGarment.textContent = this.formatMoney(this.garmentPrice);
        this.summaryTotal.textContent = this.formatMoney(this.garmentPrice + printCents);
      }

      // ---- Validation & submit ---------------------------------------------
      validate() {
        if (this.querySelector('.pc-front-print:checked') && !this.hasArtworkOrText('front')) {
          return this.i18n.frontRequired;
        }
        return null;
      }

      hasArtworkOrText(side) {
        const file = this.querySelector(`.pc-file[data-position="${side}"]`);
        const text = this.querySelector(`.pc-text-input[data-position="${side}"]`);
        return (file && file.files.length > 0) || (text && text.value.trim() !== '');
      }

      async onSubmit() {
        this.clearError();
        const err = this.validate();
        if (err) return this.showError(err);

        const variantId = this.productForm && this.productForm.querySelector('[name="id"]')
          ? this.productForm.querySelector('[name="id"]').value
          : null;
        if (!variantId) return this.showError(this.i18n.selectVariant);

        const qtyInput = this.productForm.querySelector('[name="quantity"]');
        const quantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
        const ref = this.uuid();
        const addons = this.selectedAddons();

        this.setLoading(true);
        try {
          // 1) Hat line (multipart — carries files + text + labels + _ref).
          //    Request sections here only when there are no add-on lines to add afterwards.
          const garmentResponse = await this.addGarment(variantId, quantity, ref, addons.length === 0);

          // 2) One line item per selected add-on (this request carries the refreshed sections).
          let lastResponse = garmentResponse;
          if (addons.length) {
            const addonsResponse = await this.addAddons(addons, ref);
            // The items[] response has no top-level `key`; cart-notification/drawer render off it,
            // so point them at the hat line (which exists in the freshly rendered sections).
            addonsResponse.key = garmentResponse.key;
            lastResponse = addonsResponse;
          }
          this.refreshCart(lastResponse);
        } catch (e) {
          console.error(e);
          this.showError(this.i18n.addFailed);
        } finally {
          this.setLoading(false);
        }
      }

      buildGarmentFormData(variantId, quantity, ref, includeSections) {
        const fd = new FormData();
        fd.append('id', variantId);
        fd.append('quantity', quantity);
        fd.append('properties[_ref]', ref);

        if (this.querySelector('.pc-front-print:checked')) {
          fd.append(`properties[${this.i18n.cartPositionFront}]`, this.i18n.cartFrontValue);
        }
        this.querySelectorAll('.pc-text-input').forEach((t) => {
          if (t.value.trim()) fd.append(`properties[${t.dataset.prop}]`, t.value.trim());
        });
        this.querySelectorAll('.pc-check:checked').forEach((c) => {
          if (c.classList.contains('pc-front-print')) return; // already added as the position property
          fd.append(`properties[${c.dataset.label}]`, this.i18n.cartYes);
        });
        this.querySelectorAll('.pc-file').forEach((input) => {
          if (input.files.length) fd.append(`properties[${input.dataset.prop}]`, input.files[0]);
        });

        if (includeSections) this.appendSections(fd);
        return fd;
      }

      async addGarment(variantId, quantity, ref, includeSections) {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type']; // let the browser set the multipart boundary
        config.body = this.buildGarmentFormData(variantId, quantity, ref, includeSections);
        const response = await fetch(this.cartAddUrl, config);
        const data = await response.json();
        if (data.status) throw new Error(data.description || data.message);
        return data;
      }

      async addAddons(addons, ref) {
        const items = addons.map((a) => ({
          id: this.addons[a.sku].id,
          quantity: 1,
          properties: { _ref: ref, _label: a.text ? `${a.label}: ${a.text}` : a.label },
        }));
        const body = { items };
        const cart = this.cartElement();
        if (cart) {
          body.sections = cart.getSectionsToRender().map((s) => s.id);
          body.sections_url = window.location.pathname;
        }
        const response = await fetch(`${routes.cart_add_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(body),
        });
        const data = await response.json();
        if (data.status) throw new Error(data.description || data.message);
        return data;
      }

      appendSections(fd) {
        const cart = this.cartElement();
        if (!cart) return;
        fd.append('sections', cart.getSectionsToRender().map((s) => s.id));
        fd.append('sections_url', window.location.pathname);
      }

      cartElement() {
        return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      }

      refreshCart(response) {
        const cart = this.cartElement();
        if (cart && response && response.sections) {
          cart.renderContents(response);
        } else {
          window.location = routes.cart_url;
          return;
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'hats-print-customizer', cartData: response });
        }
      }

      // ---- Helpers ----------------------------------------------------------
      setLoading(loading) {
        this.submitButton.classList.toggle('loading', loading);
        this.submitButton.setAttribute('aria-disabled', loading);
        if (this.spinner) this.spinner.classList.toggle('hidden', !loading);
      }

      showError(message) {
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
        this.errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      clearError() {
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
      }

      uuid() {
        if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
        return 'hpc-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
      }

      formatMoney(cents) {
        const format = this.moneyFormat || '{{amount}}';
        const value = (cents / 100).toFixed(2);
        const withComma = value.replace('.', ',');
        const noDec = Math.round(cents / 100).toString();
        const out = format
          .replace(/\{\{\s*amount\s*\}\}/, value)
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, withComma)
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/, noDec)
          .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, noDec);
        // money_format may include HTML/entities; strip them since we render as textContent.
        return out
          .replace(/<[^>]+>/g, '')
          .replace(/&nbsp;/g, ' ')
          .replace(/&amp;/g, '&')
          .trim();
      }
    }
  );
}
